import type { Transport } from '@fafi/shared';

// Browser WebSocket → Transport (M2-S9). Sends are queued until the socket opens (the join/hello
// handshake is written straight after construction); inbound frames arrive as fresh ArrayBuffers, so no
// copying is needed. Browsers only permit close codes 1000 / 3000–4999 from script — close() maps
// anything else to a plain close.

export class WsTransport implements Transport {
  private readonly ws: WebSocket;
  private readonly sendQ: Uint8Array[] = [];
  private opened = false;
  private msgHandler: ((bytes: Uint8Array) => void) | null = null;
  private closeHandler: ((code: number) => void) | null = null;
  private openHandler: (() => void) | null = null;

  constructor(url: string) {
    this.ws = new WebSocket(url);
    this.ws.binaryType = 'arraybuffer';
    this.ws.onopen = (): void => {
      this.opened = true;
      for (const b of this.sendQ) this.ws.send(b);
      this.sendQ.length = 0;
      this.openHandler?.();
    };
    this.ws.onmessage = (e: MessageEvent): void => {
      if (e.data instanceof ArrayBuffer) this.msgHandler?.(new Uint8Array(e.data));
    };
    this.ws.onclose = (e: CloseEvent): void => this.closeHandler?.(e.code);
    this.ws.onerror = (): void => {
      /* onclose always follows; the Connection state machine handles it */
    };
  }

  send(bytes: Uint8Array): void {
    if (this.opened && this.ws.readyState === WebSocket.OPEN) this.ws.send(bytes);
    else this.sendQ.push(bytes);
  }
  onMessage(handler: (bytes: Uint8Array) => void): void {
    this.msgHandler = handler;
  }
  onClose(handler: (code: number) => void): void {
    this.closeHandler = handler;
  }
  onOpen(handler: () => void): void {
    this.openHandler = handler;
    if (this.opened) handler();
  }
  close(code = 1000): void {
    const legal = code === 1000 || (code >= 3000 && code <= 4999);
    this.ws.close(legal ? code : undefined);
  }
  bufferedAmount(): number {
    return this.ws.bufferedAmount;
  }
}
